import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ElectionCategory } from '../../constants/categories';
import { OrderCommissionStatus } from '../../constants/commission';

export interface PartnerOrderItem {
  id: string;
  order_id: string;
  plan_name: string;
  plan_id: string;
  // The category of the plan bought; null for a plan without a category (migration 0029)
  category: ElectionCategory | null;
  channel?: 'creator' | 'channel_partner';
  // Phase 9: Custom Promo Codes discount percent, e.g. "40.00" or null
  discount_percent?: string | null;
  promo_code: string;
  promo_code_id: string;
  // Rupee decimal strings, e.g. "499.00"
  gross_amount: string;
  discount_amount: string;
  net_amount: string;
  status: string; // paid | failed | refunded | cancelled | ...
  paid_at: string;
  partner: { id: string; name: string; mobile: string };
  synced_at: string;
  status_changed_at: string | null;

  // Partner Phase Two: the earned commission (null when none yet), and its status
  commission_amount: string | null;
  commission_status: OrderCommissionStatus;
}

export interface PartnerOrderTotals {
  order_count: number;
  gross_amount: string;
  discount_amount: string;
  net_amount: string;
}

export interface PartnerOrderState {
  items: PartnerOrderItem[];
  // Covers paid orders matching the filters only (not the page)
  totals: PartnerOrderTotals;
  // Rows matching the filters, any status, used for paging
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

const ZERO_TOTALS: PartnerOrderTotals = {
  order_count: 0,
  gross_amount: '0.00',
  discount_amount: '0.00',
  net_amount: '0.00',
};

const initialState: PartnerOrderState = {
  items: [],
  totals: ZERO_TOTALS,
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,
};

const partnerOrderSlice = createSlice({
  name: 'partnerOrders',
  initialState,
  reducers: {
    setPartnerOrderLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPartnerOrderError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPartnerOrderPage: (
      state,
      action: PayloadAction<{
        items: PartnerOrderItem[];
        totals: PartnerOrderTotals;
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      const { items, totals, total, limit, offset } = action.payload;
      state.items = items;
      state.totals = totals;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
  },
});

export const { setPartnerOrderLoading, setPartnerOrderError, setPartnerOrderPage } =
  partnerOrderSlice.actions;

export default partnerOrderSlice.reducer;
